import React, { useEffect, useState } from "react";
import config from "@/config";
import {
  SYSTEM_ENVIRONMENTS,
  SYSTEM_ENVIRONMENT_PURPOSE_MAX_LENGTH,
  getSystemNameForHostname,
} from "./system-environment-hosts.js";
import "./system-environment-badge.less";

export const SYSTEM_ENVIRONMENT_CONFIG_URL = "/system-environment.json";
export { SYSTEM_ENVIRONMENT_PURPOSE_MAX_LENGTH };
export const SYSTEM_ENVIRONMENT_LABEL_UPDATE_REQUIRED = "Deployment label needs updating";

export const getSystemEnvironmentForHostname = (
  hostname,
  applicationVersion = config.VERSION,
  applicationBuildId = config.BUILD_ID,
) => {
  const systemName = getSystemNameForHostname(hostname);

  if (!systemName) {
    return null;
  }

  return {
    systemName,
    version: applicationVersion,
    buildId: applicationBuildId,
    purpose: SYSTEM_ENVIRONMENT_LABEL_UPDATE_REQUIRED,
    theme: SYSTEM_ENVIRONMENTS[systemName],
    labelCurrent: false,
  };
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const validateSystemEnvironmentConfig = (
  value,
  applicationVersion,
  applicationBuildId = config.BUILD_ID,
) => {
  if (!isPlainObject(value)) {
    return null;
  }

  const systemName = typeof value.systemName === "string" ? value.systemName.trim() : "";
  const version = typeof value.version === "string" ? value.version.trim() : "";
  const buildId = typeof value.buildId === "string" ? value.buildId.trim() : "";
  const purpose = typeof value.purpose === "string" ? value.purpose.trim() : "";
  const theme = SYSTEM_ENVIRONMENTS[systemName];

  if (
    !theme ||
    !version ||
    version !== applicationVersion ||
    !buildId ||
    buildId !== applicationBuildId ||
    !purpose ||
    purpose.length > SYSTEM_ENVIRONMENT_PURPOSE_MAX_LENGTH ||
    /[\r\n]/.test(purpose)
  ) {
    return null;
  }

  return { systemName, version, buildId, purpose, theme };
};

export const loadSystemEnvironmentConfig = async ({ signal, hostname = window.location.hostname } = {}) => {
  const fallback = getSystemEnvironmentForHostname(hostname);

  if (!fallback) {
    return null;
  }

  try {
    const response = await fetch(SYSTEM_ENVIRONMENT_CONFIG_URL, {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal,
    });

    if (response.status === 204 || !response.ok) {
      return fallback;
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.toLowerCase().includes("application/json")) {
      return fallback;
    }

    const deployment = validateSystemEnvironmentConfig(
      await response.json(),
      config.VERSION,
      config.BUILD_ID,
    );

    return deployment?.systemName === fallback.systemName
      ? { ...deployment, labelCurrent: true }
      : fallback;
  } catch {
    return fallback;
  }
};

const SystemEnvironmentBadge = () => {
  const [environment, setEnvironment] = useState(() =>
    getSystemEnvironmentForHostname(window.location.hostname),
  );

  useEffect(() => {
    const controller = new AbortController();

    loadSystemEnvironmentConfig({ signal: controller.signal }).then((value) => {
      if (!controller.signal.aborted) {
        setEnvironment(value);
      }
    });

    return () => controller.abort();
  }, []);

  if (!environment) {
    return null;
  }

  const summary = `${environment.version} · ${environment.purpose}`;

  return (
    <div
      className={`system-environment-badge system-environment-badge--${environment.theme}`}
      aria-label={`${environment.systemName}, version ${environment.version}, ${environment.purpose}`}
      data-system-environment={environment.theme}
      data-label-status={environment.labelCurrent ? "current" : "update-required"}
    >
      <div className="system-environment-badge__name">{environment.systemName}</div>
      <div className="system-environment-badge__summary" title={summary}>
        {summary}
      </div>
    </div>
  );
};

export default SystemEnvironmentBadge;
